import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { PrintContext } from "../../ChartApp";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, } from "chart.js";
import { ChoroplethController, ProjectionScale, ColorScale, GeoFeature, topojson, } from "chartjs-chart-geo";
import ChartDataLabels from "chartjs-plugin-datalabels";
import * as PolandVoivodeshipsTopojson from "../../resources/wojewodztwa-min.json";
import { convertToPercent, nbsp } from "../../utilities";
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, 
// chartjs-chart-geo features
ChoroplethController, ProjectionScale, ColorScale, GeoFeature);
function processTopoJson(initialData) {
    const processed = initialData;
    return processed;
}
export function MapChart({ multiSelects, selectedChart, chosenValue, mockDataCategory, }) {
    let topoJson = processTopoJson(PolandVoivodeshipsTopojson);
    const getMapDataValue = (filteredData, voivodeship) => {
        const v = filteredData === null || filteredData === void 0 ? void 0 : filteredData.find((d) => d.label === voivodeship);
        if (v) {
            return v.value;
        }
        return undefined;
    };
    // test
    const filteredMap = () => {
        var _a, _b;
        let dataForMap = {
            label: "",
            data: [],
        };
        if (Object.keys(multiSelects).length !== 0 &&
            ((_a = multiSelects["year"]) === null || _a === void 0 ? void 0 : _a.length) !== 0) {
            const selectedYearLabels = ((_b = multiSelects.year) === null || _b === void 0 ? void 0 : _b.map((option) => {
                return option.label;
            })) || [];
            let chartData = mockDataCategory === null || mockDataCategory === void 0 ? void 0 : mockDataCategory.values.find((value) => value.id === chosenValue.chosenValueId);
            const filteredByYear = chartData.data.find((item) => selectedYearLabels.includes(item.year.toString()));
            if (!filteredByYear) {
                return undefined;
            }
            const obj = filteredByYear.value;
            const yearLabel = filteredByYear.year;
            dataForMap.label = yearLabel;
            let voivodeshipsLabels = [];
            let voivodeshipsValues = [];
            if (obj === null) {
                return;
            }
            else {
                for (const key in obj) {
                    if (Object.prototype.hasOwnProperty.call(obj, key)) {
                        const element = obj[key];
                        voivodeshipsLabels.push(key);
                        voivodeshipsValues.push(element);
                        dataForMap.data.push({
                            label: key,
                            value: element,
                        });
                    }
                }
            }
            return dataForMap;
        }
        return undefined;
    };
    const newMultiSelects = Object.assign({}, multiSelects);
    const chartRef = useRef();
    const [mapData, setMapData] = useState([]);
    const filteredData = useMemo(() => filteredMap(), [newMultiSelects, chosenValue, mockDataCategory]);
    // https://codepen.io/stockinail/pen/ExJWLKE - https://github.com/sgratzl/chartjs-chart-geo/issues/225
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        showOutline: true,
        showGraticule: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                // enabled: true,
                callbacks: {
                    label: function (context) {
                        if (filteredData) {
                            // NOTE: see properties of context for alternative ways of constructing label
                            const thisElement = context.dataset.data[context.dataIndex];
                            const voivodeship = thisElement.feature.properties.nazwa;
                            // Alternative way with value;
                            // const value = thisElement.value;
                            // return `${voivodeship}: ${formatterValues(value)}`;
                            return voivodeship;
                        }
                        else {
                            return "Wybierz rok aby wyświetlić dane";
                        }
                    },
                },
            },
            datalabels: {
                align: "center",
                formatter: (v) => {
                    if (v.value) {
                        return convertToPercent(v.value);
                    }
                    return "";
                },
                color: function (context) {
                    if (context.dataset.data) {
                        const allValues = context.dataset.data.map(({ value }) => value);
                        const value = allValues[context.dataIndex];
                        const valueMin = Math.min(...allValues);
                        const valueMax = Math.max(...allValues);
                        const middleColorValue = (valueMin + valueMax) / 2;
                        return value > middleColorValue ? "white" : "black";
                    }
                },
                font: {
                    size: 16,
                    weight: 500,
                },
            },
        },
        scales: {
            projection: {
                axis: "x",
                projection: "mercator",
            },
            color: {
                axis: "x",
                interpolate: "#2e75b6",
                missing: "white",
                display: filteredData ? true : false,
                legend: {
                    position: "top-right",
                },
                quantize: 0,
                ticks: {
                    callback: function (value, index, values) {
                        if (filteredData) {
                            const dataValue = filteredData.data.map(({ value }) => value);
                            let minValue;
                            let maxValue;
                            if (dataValue.length) {
                                minValue = Math.min(...dataValue);
                                maxValue = Math.max(...dataValue);
                            }
                            else {
                                minValue = Math.min(0);
                                maxValue = Math.max(0);
                            }
                            if (index === 0) {
                                return convertToPercent(minValue);
                            }
                            else if (index === values.length - 1) {
                                return convertToPercent(maxValue);
                            }
                        }
                    },
                },
            },
        },
    };
    useEffect(() => {
        const dataTopo = topojson.feature(topoJson, topoJson.objects["wojewodztwa-min"]).features;
        setMapData(dataTopo);
    }, []);
    useEffect(() => {
        if (chartRef.current) {
            const instanceNotNull = chartRef.current;
            // without this changing between different charts after initial render didn't work
            instanceNotNull.update();
        }
        else {
            console.log("Chart ref not set");
        }
    }, [filteredData]);
    const printRef = useContext(PrintContext);
    return (React.createElement("section", { className: "chartBox chartBox--map card", ref: printRef },
        React.createElement("h3", { className: "text-gray-300" }, nbsp(`Wskaźnik ubóstwa energetycznego - ${chosenValue.chosenValueLabel} (Województwa)`)),
        React.createElement("div", { className: "bar" },
            React.createElement("div", { className: "chart position-relative", style: {
                    top: `0`,
                } },
                React.createElement(Chart, { ref: chartRef, type: "choropleth", data: {
                        labels: mapData.map((d) => d.properties.name),
                        datasets: [
                            {
                                outline: mapData,
                                borderWidth: 1,
                                borderColor: filteredData && (filteredData === null || filteredData === void 0 ? void 0 : filteredData.data.length) !== 0
                                    ? "white"
                                    : "rgb(118, 118, 118)",
                                data: mapData === null || mapData === void 0 ? void 0 : mapData.map((d) => ({
                                    feature: d,
                                    value: getMapDataValue(filteredData === null || filteredData === void 0 ? void 0 : filteredData.data, d.properties.nazwa),
                                })),
                            },
                        ],
                    }, 
                    // @ts-ignore
                    options: options, plugins: [ChartDataLabels] })),
            React.createElement("img", { id: "printImg", className: "d-none w-100" }))));
}
